// src/Dashboard.jsx
import * as React from "react";
import { useRedirect } from "react-admin";
import {
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Typography,
  Box,
  Chip,
  Button,
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import ShieldIcon from "@mui/icons-material/Shield";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import ArticleIcon from "@mui/icons-material/Article";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventNoteIcon from "@mui/icons-material/EventNote";
import Person2Icon from "@mui/icons-material/Person2";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const StatCard = ({ title, subtitle, icon, to }) => {
  const redirect = useRedirect();

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        height: "100%",
      }}
    >
      <CardActionArea
        onClick={() => redirect(to)}
        sx={{
          height: "100%",
          "&:hover .hoverLift": { transform: "translateY(-2px)" },
        }}
      >
        <CardContent className="hoverLift" sx={{ transition: "transform .15s ease" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: "action.hover",
              }}
            >
              {icon}
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={800} noWrap>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {subtitle}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const CurrentSeasonBanner = () => {
  const redirect = useRedirect();
  const [season, setSeason] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const token = localStorage.getItem("basicAuth");
    fetch("/api/admin/seasons", { headers: { Authorization: `Basic ${token}` } })
      .then((res) => res.json())
      .then((data) => setSeason(data.find((s) => s.is_current) ?? null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        mb: 2,
        bgcolor: "primary.main",
        color: "primary.contrastText",
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <Box
              component="img"
              src="/logo.png"
              alt="Dinamo Ostia"
              sx={{ width: 44, height: 44, borderRadius: "50%", bgcolor: "white", p: 0.5 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" fontWeight={900}>
                Dinamo Ostia — Admin
              </Typography>
              {season ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <Chip
                    size="small"
                    icon={<CheckCircleIcon sx={{ color: "inherit !important" }} />}
                    label={`Stagione corrente: ${season.label}`}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.15)",
                      color: "inherit",
                      fontWeight: 700,
                      "& .MuiChip-icon": { color: "inherit" },
                    }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                  Nessuna stagione attiva — configurala in "Stagioni".
                </Typography>
              )}
            </Box>
          </Box>

          <Button
            variant="outlined"
            size="small"
            onClick={() => redirect("/admin/seasons")}
            sx={{
              color: "inherit",
              borderColor: "rgba(255,255,255,0.5)",
              flexShrink: 0,
              "&:hover": { borderColor: "white", bgcolor: "rgba(255,255,255,0.08)" },
            }}
          >
            Gestisci stagioni
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default function Dashboard() {
  return (
    <Box sx={{ p: 2 }}>
      <CurrentSeasonBanner />

      <Typography variant="overline" sx={{ opacity: 0.6, fontWeight: 700, pl: 0.5 }}>
        Stagione
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Stagioni"
            subtitle="Crea, attiva, gestisci"
            icon={<CalendarMonthIcon />}
            to="/admin/seasons"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Partite"
            subtitle="Formazione, eventi, convocati"
            icon={<SportsSoccerIcon />}
            to="/admin/matches"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Calendario girone"
            subtitle="Tutte le squadre, giornata per giornata"
            icon={<EventNoteIcon />}
            to="/admin/league-fixtures"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Rosa"
            subtitle="Numeri di maglia, ruoli, copia da stagione"
            icon={<Person2Icon />}
            to="/admin/roster"
          />
        </Grid>
      </Grid>

      <Typography variant="overline" sx={{ opacity: 0.6, fontWeight: 700, pl: 0.5 }}>
        Anagrafica e contenuti
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Giocatori"
            subtitle="Lista, crea, modifica"
            icon={<GroupsIcon />}
            to="/admin/players"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avversari"
            subtitle="Squadre e loghi"
            icon={<ShieldIcon />}
            to="/admin/opponents"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="News"
            subtitle="Titoli, testo, pubblicazione"
            icon={<ArticleIcon />}
            to="/admin/news"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Contatti"
            subtitle="Richieste dal sito"
            icon={<MailOutlineIcon />}
            to="/admin/contacts"
          />
        </Grid>
      </Grid>
    </Box>
  );
}
